import { fromUrlToObj, getSeoData } from './index';
import { getStaticData } from './getStaticData';
import {
  getDataForTransformationsQuery,
  searchItemsQuery,
  searchVippedAllQuery
} from '../../api/queries';
import { getBreadcrumbs } from './getBreadcrumbs';
import { getSearchResultInitialQueryParams } from '../../hooks/useSearchResultItems';
import { formatSearchResultSeoData } from './seo';
import { setCurrentFiltersToStore } from './search';
import { buildSearchPaginationUrls } from './buildSearchPaginationUrls';

export const fetchSearchCursorPageProps = async ({
  apolloClient,
  query,
  asPath,
  res
}) => {
  const afterCursor = query.after;
  const prevCursor = query.prev || null;

  const [dataForTransform] = await Promise.all([
    apolloClient.query({ query: getDataForTransformationsQuery }),
    getStaticData(apolloClient)
  ]);

  const transformedObj = fromUrlToObj(dataForTransform, query);

  if (transformedObj.noMatch) {
    if (res) res.statusCode = 404;
    return { statusCode: 404 };
  }

  // eslint-disable-next-line camelcase
  const { category_touched, ...filtersWithoutCategoryTouched } =
    transformedObj.filter;
  transformedObj.filter = filtersWithoutCategoryTouched;

  const { itemsQueryVariables } = getSearchResultInitialQueryParams(
    transformedObj.filter,
    transformedObj.sorting,
    afterCursor
  );

  const searchResult = await apolloClient.query({
    query: searchItemsQuery,
    variables: itemsQueryVariables
  });

  const pageInfo = searchResult.data?.itemsConnection?.pageInfo;
  const items =
    searchResult.data?.itemsConnection?.edges?.map(e => e.node) ?? [];

  const { nextPageUrl, prevPageUrl, canonicalUrl } = buildSearchPaginationUrls({
    asPath,
    afterCursor,
    prevCursor,
    pageInfo
  });

  if (res) {
    res.setHeader(
      'Cache-Control',
      'public, s-maxage=3600, stale-while-revalidate=3600, max-age=0'
    );
  }

  return {
    isPaginationPage: true,
    items,
    nextPageUrl,
    prevPageUrl,
    canonicalUrl
  };
};

export const fetchRegularSearchPageProps = async ({
  apolloClient,
  query,
  asPath,
  req,
  res,
  store,
  currentLang
}) => {
  const [dataForTransform, rawSeoData] = await Promise.all([
    apolloClient.query({ query: getDataForTransformationsQuery }),
    getSeoData(apolloClient, asPath, req),
    // getStaticData primes the Apollo cache on the server.
    // Without this call, the data won't be included in the final HTML,
    // which is critical for search engine indexing
    getStaticData(apolloClient)
  ]);

  const transformedObj = fromUrlToObj(dataForTransform, query);

  if (transformedObj.noMatch) {
    if (res) res.statusCode = 404;
    return { statusCode: 404 };
  }

  const formattedSeoData = formatSearchResultSeoData(
    rawSeoData,
    currentLang,
    dataForTransform.data,
    transformedObj.filter
  );

  const { breadcrumbs } = getBreadcrumbs(
    dataForTransform?.data,
    transformedObj.filter,
    'search-result'
  );

  setCurrentFiltersToStore(transformedObj, store, dataForTransform);

  // eslint-disable-next-line camelcase
  const { category_touched, ...filtersWithoutCategoryTouched } =
    transformedObj.filter;
  transformedObj.filter = filtersWithoutCategoryTouched;

  const { itemsQueryVariables, vipAllQueryVariables } =
    getSearchResultInitialQueryParams(
      transformedObj.filter,
      transformedObj.sorting,
      null
    );

  const [searchResult] = await Promise.all([
    apolloClient.query({
      query: searchItemsQuery,
      variables: itemsQueryVariables
    }),
    apolloClient.query({
      query: searchVippedAllQuery,
      variables: vipAllQueryVariables
    })
  ]);

  const pageInfo = searchResult.data?.itemsConnection?.pageInfo;
  const totalCount = searchResult.data?.itemsConnection?.totalCount ?? null;

  const { nextPageUrl, prevPageUrl, canonicalUrl } = buildSearchPaginationUrls({
    asPath,
    afterCursor: null,
    prevCursor: null,
    pageInfo,
    hostSum: rawSeoData.hostSum
  });

  return {
    transformedObj,
    seoData: formattedSeoData,
    h1: formattedSeoData.h1,
    isSearchPage: true,
    breadcrumbs,
    currentLang,
    nextPageUrl,
    prevPageUrl,
    noindex: totalCount === 0,
    canonicalUrl
  };
};
